import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Clock,
  DollarSign,
  FileCheck,
  FileClock,
  FileText,
  Package,
  PackageCheck,
  Search,
  Timer,
  X,
} from "lucide-react";
import { KPICard } from "../../components/purchaseOrder/KPICard";
import { POTable } from "../../components/purchaseOrder/POTable";
import { InvoiceTable } from "../../components/purchaseOrder/InvoiceTable";
import { AssetTable } from "../../components/purchaseOrder/AssetTable";
import { PurchaseOrder } from "../../types/purchaseOrder";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useToast } from "../../hooks/use-toast";
import apiService from "../../services/apiService";
import { enrichAssetsWithShipmentInsurance } from "../../lib/assetInsurance";
import { formatCurrencyINR } from "../../lib/enhanced-currency-utils";
import { InvoiceViewSheet } from "../../components/purchaseOrder/InvoiceDetailDialog";
import { AssetDetailSidebar } from "../../components/purchaseOrder/AssetDetailSidebar";
import { cn } from "../../lib/utils";

interface AdminUserList {
  id: number;
  name: string;
}

const getPoId = (po: PurchaseOrder) =>
  String((po as PurchaseOrder & { id?: string | number }).id ?? po.poId);

const ManagePurchaseOrder = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("po");
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[] | []>(
    []
  );
  const [pagination, setPagination] = useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 25,
    totalPages: 1,
  });
  const [invoicePagination, setInvoicePagination] = useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 25,
    totalPages: 1,
  });
  const [assetPagination, setAssetPagination] = useState({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 25,
    totalPages: 1,
  });
  const [invoices, setInvoices] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);
  const [poSummary, setPoSummary] = useState<any>();
  const [invoiceSearchQuery, setInvoiceSearchQuery] = useState("");
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [assetLoading, setAssetLoading] = useState(false);
  const [invoiceSummary, setInvoiceSummary] = useState<any>();
  const [assetSummary, setAssetSummary] = useState<any>();
  const [selectedInvoicepo, setSelectedInvoicePo] = useState<any>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const [adminUserList, setAdminUserList] = useState<AdminUserList[]>([]);
  const [assetsCategories, setAssetsCategories] = useState<any[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [poPendingDuplicate, setPoPendingDuplicate] = useState<PurchaseOrder | null>(null);
  const [isDuplicatingPo, setIsDuplicatingPo] = useState(false);
  const poSearchInputRef = useRef<HTMLInputElement>(null);
  const invoiceSearchInputRef = useRef<HTMLInputElement>(null);
  const assetSearchInputRef = useRef<HTMLInputElement>(null);

  const handleViewDetails = (po: PurchaseOrder) => {
    if (po.customerId) {
      navigate(`/manage-customer/${po.customerId}`, {
        state: { tab: "purchase-order", poDetails: po },
      });
    } else {
      toast({
        title: "Error",
        description: "Customer ID not found for this purchase order",
        variant: "destructive",
      });
    }
  };

  const handleAssetViewDetails = (asset: any) => {
    setSelectedAsset(asset);
    setAssetOpen(true);
  };

  const fetchAdminUserList = async () => {
    try {
      const response = await apiService.get<{ data: AdminUserList[] }>(
        `/customers/get-admin-user-list`
      );
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id,
      }));
      setAdminUserList(transformedDocs);
    } catch (error) {
      console.error("Error fetching admin users:", error);
    }
  };

  const getPOByInvoiceId = async (invoiceId: string) => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-purchase-order-by-invoiced-id/${invoiceId}`
      );
      if (response && response.data) {
        setSelectedInvoicePo(response.data);
        return response.data;
      }
      return null;
    } catch (error) {
      console.error("Error fetching PO by invoice ID:", error);
      toast({
        title: "Error",
        description: "Failed to fetch purchase order for this invoice",
        variant: "destructive",
      });
      return null;
    }
  };

  const handleInvoiceViewDetails = async (invoice: any) => {
    setSelectedInvoice(invoice);
    setOpen(true);
    await getPOByInvoiceId(invoice.po_id);
  };

  const handleKPIClick = (kpiType: string, tab?: string) => {
    if (tab) setActiveTab(tab);
    toast({
      title: "Filter Applied",
      description: `Showing ${kpiType} records`,
    });
  };

  useEffect(() => {
    fetchAdminUserList();
    fetchAssetsCategories();
  }, []);

  const fetchAssetsCategories = async () => {
    const response = await apiService.get<any>(
      `/purchase-order/get-asset-categories-list`
    );
    setAssetsCategories(response.data);
  };

  useEffect(() => {
    fetchPO();
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchPO();
      } else {
        setPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPO = async () => {
    try {
      setLoading(true);
      const response = await apiService.post<any>(`/purchase-order/list`, {
        search: searchQuery || undefined,
        pagination: {
          pageSize: pagination.pageSize,
          currentPage: pagination.currentPage,
        },
        status: undefined,
      });

      if (response && response.data && response.pagination) {
        setPurchaseOrders(response.data);
        setPagination({
          totalRecords: response.pagination.totalRecords,
          currentPage: response.pagination.currentPage,
          pageSize: response.pagination.pageSize,
          totalPages: response.pagination.totalPages,
        });
      } else if (response && Array.isArray(response)) {
        setPurchaseOrders(response);
      } else if (response && response.data) {
        setPurchaseOrders(Array.isArray(response.data) ? response.data : []);
      } else {
        setPurchaseOrders([]);
      }
    } catch (error) {
      console.error("Error fetching purchase orders:", error);
      toast({
        title: "Error",
        description: "Failed to fetch purchase orders",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize,
    }));
  };

  const confirmDuplicatePurchaseOrder = async () => {
    if (!poPendingDuplicate) return;

    const poId = getPoId(poPendingDuplicate);
    if (!poId) {
      toast({
        title: "Error",
        description: "Purchase order ID not found",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDuplicatingPo(true);
      await apiService.post(`/purchase-order/duplicate-po/${poId}`, {});
      toast({
        title: "Success",
        description: "Purchase order duplicated successfully",
      });
      setPoPendingDuplicate(null);
      await fetchPO();
      await fetchPoSummary();
    } catch (error) {
      console.error("Failed to duplicate purchase order:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate purchase order",
        variant: "destructive",
      });
    } finally {
      setIsDuplicatingPo(false);
    }
  };

  const handleInvoicePageChange = (page: number, pageSize: number) => {
    setInvoicePagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize,
    }));
  };

  const handleAssetPageChange = (page: number, pageSize: number) => {
    setAssetPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize,
    }));
  };

  useEffect(() => {
    fetchInvoices();
  }, [invoicePagination.currentPage, invoicePagination.pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (invoicePagination.currentPage === 1) {
        fetchInvoices();
      } else {
        setInvoicePagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [invoiceSearchQuery]);

  useEffect(() => {
    fetchAssets();
  }, [assetPagination.currentPage, assetPagination.pageSize]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (assetPagination.currentPage === 1) {
        fetchAssets();
      } else {
        setAssetPagination((prev) => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [assetSearchQuery]);

  const fetchInvoices = async () => {
    try {
      setInvoiceLoading(true);
      const response = await apiService.post<any>(
        `/purchase-order/get-all-invoices`,
        {
          search: invoiceSearchQuery || undefined,
          pagination: {
            pageSize: invoicePagination.pageSize,
            currentPage: invoicePagination.currentPage,
          },
          status: undefined,
        }
      );

      if (response && response.data && response.pagination) {
        setInvoices(response.data);
        setInvoicePagination({
          totalRecords: response.pagination.totalRecords,
          currentPage: response.pagination.currentPage,
          pageSize: response.pagination.pageSize,
          totalPages: response.pagination.totalPages,
        });
      } else if (response && Array.isArray(response)) {
        setInvoices(response);
      } else if (response && response.data) {
        setInvoices(Array.isArray(response.data) ? response.data : []);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast({
        title: "Error",
        description: "Failed to fetch invoices",
        variant: "destructive",
      });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      setAssetLoading(true);
      const response = await apiService.post<any>(
        `/purchase-order/get-all-individual-assets`,
        {
          search: assetSearchQuery || undefined,
          pagination: {
            pageSize: assetPagination.pageSize,
            currentPage: assetPagination.currentPage,
          },
          status: undefined,
        }
      );

      if (response && response.data && response.pagination) {
        const withInsurance = await enrichAssetsWithShipmentInsurance(response.data);
        setAssets(withInsurance);
        setAssetPagination({
          totalRecords: response.pagination.totalRecords,
          currentPage: response.pagination.currentPage,
          pageSize: response.pagination.pageSize,
          totalPages: response.pagination.totalPages,
        });
      } else if (response && Array.isArray(response)) {
        setAssets(await enrichAssetsWithShipmentInsurance(response));
      } else if (response && response.data) {
        const assets = Array.isArray(response.data) ? response.data : [];
        setAssets(await enrichAssetsWithShipmentInsurance(assets));
      } else {
        setAssets([]);
      }
    } catch (error) {
      console.error("Error fetching assets:", error);
      toast({
        title: "Error",
        description: "Failed to fetch assets",
        variant: "destructive",
      });
    } finally {
      setAssetLoading(false);
    }
  };

  const fetchPoSummary = async () => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-po-summary`
      );
      setPoSummary(response.data);
    } catch (error) {
      console.error("Error fetching po summary:", error);
    }
  };

  const fetchInvoiceSummary = async () => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-invoice-summary`
      );
      setInvoiceSummary(response.data);
    } catch (error) {
      console.error("Error fetching invoice summary:", error);
    }
  };

  const fetchAssetSummary = async () => {
    try {
      const response = await apiService.get<any>(
        `/purchase-order/get-asset-summary`
      );
      setAssetSummary(response.data);
    } catch (error) {
      console.error("Error fetching asset summary:", error);
    }
  };

  useEffect(() => {
    fetchPoSummary();
    fetchInvoiceSummary();
    fetchAssetSummary();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        if (activeTab === "po") poSearchInputRef.current?.focus();
        if (activeTab === "invoice") invoiceSearchInputRef.current?.focus();
        if (activeTab === "asset") assetSearchInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeTab]);

  const renderSearchInput = (
    value: string,
    onChange: (value: string) => void,
    placeholder: string,
    inputRef: React.RefObject<HTMLInputElement | null>
  ) => (
    <div
      className={cn(
        "relative w-full transition-all duration-300 xl:max-w-[360px]",
        isSearchFocused && "xl:max-w-[400px]"
      )}
    >
      <Search
        className={cn(
          "absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 transition-colors duration-300",
          isSearchFocused ? "text-[#3f8b98]" : "text-slate-400"
        )}
      />
      <Input
        ref={inputRef}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsSearchFocused(true)}
        onBlur={() => setIsSearchFocused(false)}
        className={cn(
          "h-10 w-full rounded-xl border bg-white pl-10 pr-14 text-[13px] font-medium text-slate-700 outline-none transition-all duration-300 placeholder:text-slate-400 focus-visible:ring-4 focus-visible:ring-[#c0e5e9]/24 focus-visible:ring-offset-0 focus-visible:outline-none",
          isSearchFocused
            ? "!border-[#b7dfe2] focus-visible:!ring-[#b7dfe2]/30"
            : "!border-[#d7eaee]"
        )}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-10 top-1/2 -translate-y-1/2 text-slate-400"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg border border-[#d7eaee] bg-[#f8fcfd] px-2 py-1 text-[11px] font-semibold text-slate-500">
        Ctrl K
      </div>
    </div>
  );

  return (
    <div className="min-h-screen w-full bg-[#F5F7FB]">
      <main className="w-full">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="mb-4 border-b border-slate-200 bg-white px-5 py-3 lg:px-7">
            <div className="relative flex flex-col gap-3 xl:flex-row xl:items-center">
              <div className="flex min-w-[250px] items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#eef7f8]">
                  <FileText className="h-4 w-4 text-[#5da8b4]" />
                </div>
                <div>
                  <h1 className="text-[20px] font-bold leading-none tracking-tight text-slate-800">
                    Purchase Orders
                  </h1>
                  <p className="mt-1 text-[11px] text-slate-500">
                    Manage and track all purchase orders
                  </p>
                </div>
              </div>

              <div className="flex flex-1 flex-col gap-3 xl:flex-row xl:items-center xl:justify-end">
                <div className="xl:absolute xl:left-1/2 xl:-translate-x-1/2">
                <TabsList className="grid h-auto w-full grid-cols-3 rounded-2xl border border-slate-200 bg-slate-50/90 p-1 shadow-[0_8px_30px_rgba(15,23,42,0.05)] xl:min-w-[430px]">
                  <TabsTrigger
                    value="po"
                    className="group gap-2 rounded-xl px-3.5 py-2 text-[13px] font-medium text-slate-500 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-[#5da8b4]/15"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-white text-slate-400 transition-all group-data-[state=active]:bg-[#e7f6f8] group-data-[state=active]:text-[#4d9aa6]">
                      <FileText className="h-3 w-3" />
                    </span>
                    Purchase Orders
                  </TabsTrigger>
                  <TabsTrigger
                    value="invoice"
                    className="group gap-2 rounded-xl px-3.5 py-2 text-[13px] font-medium text-slate-500 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-[#5da8b4]/15"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-white text-slate-400 transition-all group-data-[state=active]:bg-[#e7f6f8] group-data-[state=active]:text-[#4d9aa6]">
                      <DollarSign className="h-3 w-3" />
                    </span>
                    Payments
                  </TabsTrigger>
                  <TabsTrigger
                    value="asset"
                    className="group gap-2 rounded-xl px-3.5 py-2 text-[13px] font-medium text-slate-500 transition-all duration-200 data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm data-[state=active]:ring-1 data-[state=active]:ring-[#5da8b4]/15"
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-white text-slate-400 transition-all group-data-[state=active]:bg-[#e7f6f8] group-data-[state=active]:text-[#4d9aa6]">
                      <Package className="h-3 w-3" />
                    </span>
                    Assets
                  </TabsTrigger>
                </TabsList>
                </div>

                {activeTab === "po" &&
                  renderSearchInput(
                    searchQuery,
                    setSearchQuery,
                    "Search by PO ID, Supplier, or Customer...",
                    poSearchInputRef
                  )}
                {activeTab === "invoice" &&
                  renderSearchInput(
                    invoiceSearchQuery,
                    setInvoiceSearchQuery,
                    "Search by invoice number, PO ID, or Supplier...",
                    invoiceSearchInputRef
                  )}
                {activeTab === "asset" &&
                  renderSearchInput(
                    assetSearchQuery,
                    setAssetSearchQuery,
                    "Search by item ID, description, or PO ID...",
                    assetSearchInputRef
                  )}
              </div>
            </div>
          </div>

          <div className="space-y-6 px-5 pb-6 lg:px-7">
            <TabsContent value="po" className="space-y-6">
              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_28px_rgba(35,58,74,0.06)]">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <KPICard
                  title="PO Raised"
                  count={poSummary?.totalPurchaseOrders}
                  value={formatCurrencyINR(poSummary?.totalAssetLinesAmount)}
                  icon={FileText}
                  variant="amber"
                  onClick={() => handleKPIClick("all POs")}
                />
                <KPICard
                  title="Approved"
                  count={poSummary?.approved}
                  value={formatCurrencyINR(poSummary?.approvedTotalAmount)}
                  icon={FileCheck}
                  variant="teal"
                  onClick={() => handleKPIClick("approved POs")}
                />
                <KPICard
                  title="Pending"
                  count={poSummary?.pending}
                  value={formatCurrencyINR(poSummary?.pendingTotalAmount)}
                  icon={Clock}
                  variant="gold"
                  onClick={() => handleKPIClick("pending POs")}
                />
                <KPICard
                  title="Invoice Pending"
                  count={poSummary?.invoicePending}
                  value={formatCurrencyINR(poSummary?.PendingInvoiceTotalAmount)}
                  icon={FileClock}
                  variant="peach"
                  onClick={() => handleKPIClick("invoice pending")}
                />
                <KPICard
                  title="Active POs"
                  count={0}
                  value="₹0"
                  icon={AlertCircle}
                  variant="indigo"
                  onClick={() => handleKPIClick("active POs")}
                />
              </div>
              </div>

              <div>
                <POTable
                  purchaseOrders={purchaseOrders}
                  onViewDetails={handleViewDetails}
                  onDuplicatePO={setPoPendingDuplicate}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              </div>
            </TabsContent>

            <TabsContent value="invoice" className="space-y-6">
              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_28px_rgba(35,58,74,0.06)]">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <KPICard
                  title="Invoice Raised"
                  count={invoiceSummary?.totalInvoices}
                  value={formatCurrencyINR(invoiceSummary?.totalInvoiceAmount)}
                  icon={FileCheck}
                  variant="amber"
                  onClick={() => handleKPIClick("invoices raised")}
                />
                <KPICard
                  title="Invoices Pending"
                  count={invoiceSummary?.invoicePending}
                  value={formatCurrencyINR(
                    invoiceSummary?.PendingInvoiceTotalAmount
                  )}
                  icon={FileClock}
                  variant="gold"
                  onClick={() => handleKPIClick("pending invoices")}
                />
                <KPICard
                  title="Payments Paid"
                  count={invoiceSummary?.paymentsPaid}
                  value={formatCurrencyINR(
                    invoiceSummary?.paymentsPaidTotalAmount
                  )}
                  icon={DollarSign}
                  variant="teal"
                  onClick={() => handleKPIClick("paid payments")}
                />
                <KPICard
                  title="Payments Pending"
                  count={invoiceSummary?.paymentsPending}
                  value={formatCurrencyINR(
                    invoiceSummary?.PendingPaymentTotalAmount
                  )}
                  icon={AlertCircle}
                  variant="peach"
                  onClick={() => handleKPIClick("pending payments")}
                />
                <KPICard
                  title="Overdue"
                  count={invoiceSummary?.paymentsOverdue}
                  value={formatCurrencyINR(
                    invoiceSummary?.OverduePaymentTotalAmount
                  )}
                  icon={Timer}
                  variant="red"
                  onClick={() => handleKPIClick("overdue payments")}
                />
              </div>
              </div>

              <div>
                <InvoiceTable
                  purchaseOrders={purchaseOrders}
                  onViewDetails={handleInvoiceViewDetails}
                  pagination={invoicePagination}
                  onPageChange={handleInvoicePageChange}
                  loading={invoiceLoading}
                  invoices={invoices}
                />
              </div>
            </TabsContent>

            <TabsContent value="asset" className="space-y-6">
              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_12px_28px_rgba(35,58,74,0.06)]">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <KPICard
                  title="Total Assets"
                  count={assetSummary?.totalAssets}
                  value={formatCurrencyINR(assetSummary?.totalAssetAmount)}
                  icon={Package}
                  variant="amber"
                  onClick={() => handleKPIClick("all assets")}
                />
                <KPICard
                  title="Asset Delivered"
                  count={assetSummary?.assetsDelivered}
                  value={formatCurrencyINR(
                    assetSummary?.DeliveredAssetTotalAmount
                  )}
                  icon={PackageCheck}
                  variant="teal"
                  onClick={() => handleKPIClick("delivered assets")}
                />
                <KPICard
                  title="Assets Pending"
                  count={assetSummary?.assetsPending}
                  value={formatCurrencyINR(assetSummary?.PendingAssetTotalAmount)}
                  icon={Package}
                  variant="gold"
                  onClick={() => handleKPIClick("pending assets")}
                />
                <KPICard
                  title="Insurance Pending"
                  count={assetSummary?.insurancePending}
                  value={formatCurrencyINR(
                    assetSummary?.PendingInsuranceTotalAmount
                  )}
                  icon={AlertCircle}
                  variant="indigo"
                  onClick={() => handleKPIClick("insurance pending")}
                />
              </div>
              </div>

              <div>
                <AssetTable
                  onViewDetails={handleAssetViewDetails}
                  pagination={assetPagination}
                  onPageChange={handleAssetPageChange}
                  loading={assetLoading}
                  assets={assets}
                />
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </main>

      {selectedInvoice && (
        <InvoiceViewSheet
          po={selectedInvoicepo}
          invoice={selectedInvoice}
          open={open}
          onOpenChange={setOpen}
          addPayment={() => {}}
          adminUserList={adminUserList}
        />
      )}

      <AlertDialog
        open={!!poPendingDuplicate}
        onOpenChange={(open) => {
          if (!open && !isDuplicatingPo) setPoPendingDuplicate(null);
        }}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate this purchase order?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  Are you sure you want to create a duplicate of this Purchase Order?
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
                  <p>
                    <span className="font-semibold text-slate-800">PO Code:</span>{" "}
                    {poPendingDuplicate?.purchaseOrderCode || "-"}
                  </p>
                  <p className="mt-1">
                    <span className="font-semibold text-slate-800">PO ID:</span>{" "}
                    {poPendingDuplicate ? getPoId(poPendingDuplicate) : "-"}
                  </p>
                </div>
                <p>
                  A new Purchase Order will be created in Draft status with a newly
                  generated PO Code.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDuplicatingPo}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              className="border-transparent bg-[#3f8b98] text-white hover:bg-[#357a85] hover:text-white"
              disabled={isDuplicatingPo}
              onClick={() => void confirmDuplicatePurchaseOrder()}
            >
              {isDuplicatingPo ? "Duplicating…" : "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedAsset && (
        <AssetDetailSidebar
          open={assetOpen}
          onOpenChange={setAssetOpen}
          asset={selectedAsset}
          po={{
            purchaseOrderCode: selectedAsset.purchase_order_code,
            supplierId: selectedAsset.supplier_id,
            customerId: selectedAsset.customer_id,
            supplierAddress: selectedAsset.supplierAddress,
            deliveryAddress: selectedAsset.deliveryAddress,
          }}
          supplier={{
            companyName:
              selectedAsset.supplierName || selectedAsset.supplierTradeName,
          }}
          customer={{
            entityName:
              selectedAsset.customerName || selectedAsset.customerBrandName,
          }}
          isIndividualAsset={true}
          assetsCategories={assetsCategories}
          invoices={
            selectedAsset.invoice_id
              ? [
                  {
                    date: selectedAsset.date,
                    number: selectedAsset.number,
                    id: selectedAsset.invoice_id,
                  },
                ]
              : []
          }
          updateDetails={(updatedAsset) => {
            setSelectedAsset(updatedAsset);
            setAssets((prevAssets) =>
              prevAssets.map((asset) =>
                asset.id === updatedAsset.id ? { ...asset, ...updatedAsset } : asset
              )
            );
          }}
        />
      )}
    </div>
  );
};

export default ManagePurchaseOrder;
